// RUTA: src/components/ui/CountUp.tsx
//
// Anima un número de 0 (o el valor previo) al valor actual con un
// ease-out cubic en ~600ms. Da sensación de "vivo" a los contadores
// (kcal del día, racha, sesiones).
//
// Respeta prefers-reduced-motion: si está activo, salta directo al
// número final sin animar.

import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface Props {
  value: number;
  /** Duración en ms (default 600) */
  duration?: number;
  /** Formatter — default toLocaleString para separar miles */
  format?: (n: number) => string;
  /** Clase CSS extra */
  className?: string;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function CountUp({ value, duration = 600, format, className }: Props) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(value);
  const fromRef = useRef<number>(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);
      const current = Math.round(from + (to - from) * eased);
      setDisplay(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, reduced]);

  const formatted = format ? format(display) : display.toLocaleString();
  return <span className={className}>{formatted}</span>;
}
